import React from 'react';
import { useParams } from 'react-router-dom';
import { Card, Row, Col, CardSubtitle, UncontrolledTooltip } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFetch, PseudoStates } from '@cordata/buffet';
import { UncontrolledTable, SimpleCollapse } from '~/components';
import formatDate from '~/utils/format/date';

const CustodyHistory = () => {
  const params = useParams();

  return (
    <UncontrolledTable
      name="custody-history"
      endpoint={{
        url: 'data/public/list-view-cs-tbl-source-custody-history',
          params: {
            patient_uid: params.patient_id,
            'all-data': '1',
          },
      }}
      className="mt-2"
      defaultSorted={[{ id: 'custodyBeginDt', desc: true }]}
      columns={[
        {
          id: 'county',
          Header: 'County',
          accessor: 'attributes.county',
        },
        {
          id: 'custodyBeginDt',
          Header: 'Custody Begin Date',
          accessor: 'attributes.custodyBeginDt',
          cellType: 'date',
        },
        {
          id: 'custodyEndDt',
          Header: 'Custody End Date',
          accessor: 'attributes.custodyEndDt',
          cellType: { type: 'date', emptyValue: 'Current' },
        },
        {
          id: 'primaryRemovalReason',
          Header: 'Primary Removal Reason',
          accessor: 'attributes.primaryRemovalReason',
        },
        {
          id: 'secondaryRemovalReasons',
          Header: 'Secondary Removal Reason',
          accessor: 'attributes.secondaryRemovalReasons',
        },
        {
          id: 'primaryEndReason',
          Header: 'Primary End Reason',
          accessor: 'attributes.primaryEndReason',
        },
        {
          id: 'secondaryEndReasons',
          Header: 'Secondary End Reason',
          accessor: 'attributes.secondaryEndReasons',
        },
      ]}
    />
  );
};

const Custody = () => {
  const params = useParams();

  const [sourceChildStatus, sourceChild] = useFetch(
    'data/public/list-view-cs-tbl-source-child',
    {
      params: {
        patient_uid: params.patient_id,
        sort: '-custodyStartDateDt',
        'all-data': '1',
      },
      initialState: [],
      instance: 'p',
    },
  );

  const getLegalStatusInfo = legalStatus => {
    const legalStatusDescriptions = {
      'Emergency Court Order': '(ECO) Also known as ex parte order is a type of temporary order used in circumstances to address the immediate danger or imminent harm of a child.',
      'Ex Parte': 'An emergency order has been signed by a judge allowing the child to be temporarily removed from his/her legal guardian, and custody has been granted to HCJFS without dual representation.',
      'Officer Assisted Child Removal': '(OA) A child has been taken into custody as the result of a Law Enforcement official determining there are reasonable grounds to believe the child is in immediate danger or has suffered an illness or injury due to not receiving proper care.',
      'Permanent Custody': 'A court order has been granted terminating parental rights and the child is available for adoption',
      'Permanent Surrender': 'An agreement with a public children services agency (PCSA) or private child placing agency (PCPA) to voluntarily surrender a child into the permanent custody of an agency.',
      'Planned Permanent Living Arrangement': 'A court order has been granted to allow HCJFS to pursue a permanency plan other than reunification (e.g., independent living, guardianship) without termination of parental rights.',
      'Temporary Court Order': 'An initial hearing has been held to assign legal representation to the parent or caregiver of a child covered by an ex parte order, and the ex parte has been upheld.',
      'Temporary Custody': 'An adjudication hearing has been held, with evidence to substantiate allegations of abuse or neglect, and the parent or caregiver has 12 months to pursue reunification with his/her child.',
      'Temporary Custody First Extension': 'The court has granted a 6-month extension of custody to child welfare with the goal of reunification within 6 months',
      'Temporary Custody Second Extension': 'A review hearing was held, and a second extension of temporary custody has been granted for an additional 6 months, allowing the family to continue to pursue reunification.',
    };

    return (<span><b>{legalStatus} -</b> {legalStatusDescriptions[legalStatus] || 'N/A'}</span>);
  };

  return (
    <Card body className="mb-3">
      {sourceChildStatus === 'success' ? (
        sourceChild.map(sc => (
          <Row>
            <Col sm={6}>
              <CardSubtitle className="text-primary">
                Current Custody Begin Date
              </CardSubtitle>
              {formatDate(sc.attributes.custodyStartDateDt, 'MM/DD/YYYY')}
            </Col>
            <Col sm={6}>
              <CardSubtitle className="text-primary">
                Legal Status&nbsp;
                <FontAwesomeIcon icon="exclamation-circle" id="legal-status-info" />
                <UncontrolledTooltip target="legal-status-info">{getLegalStatusInfo(sc.attributes.csChildLegalStatusFull)}</UncontrolledTooltip>
              </CardSubtitle>
              {sc.attributes.csChildLegalStatusFull} {formatDate(sc.attributes.legalStartDateDt, 'MM/DD/YYYY')}
            </Col>

            <Col sm={6}>
              <CardSubtitle className="text-primary mt-3">
                Removal Reason
              </CardSubtitle>
              {sc.attributes.removalReason}
            </Col>

            <Col sm={6}>
              <CardSubtitle className="text-primary mt-3">
                Responsible School District
              </CardSubtitle>
              {sc.attributes.responsibleSchoolDistrict}
            </Col>

            <Col sm={6}>
              <CardSubtitle className="text-primary mt-3">
                First Custody Date
              </CardSubtitle>
              {formatDate(sc.attributes.firstCustodyBeginDt, 'MM/DD/YYYY')}
            </Col>

            <Col sm={6}>
              <CardSubtitle className="text-primary mt-3">
                Reference Number
              </CardSubtitle>
              {sc.attributes.ssid}
            </Col>

            <Col sm={12}>
              <CardSubtitle className="text-primary mt-3">
                Lifetime Custody Episodes
              </CardSubtitle>
              {sc.attributes.totalCustodyEpisodes}
            </Col>

            <Col sm={12}>
              <CardSubtitle className="text-primary mt-3">
                Last custody episode termination reason
              </CardSubtitle>
              {sc.attributes.prevCustodyEndReason}
            </Col>
          </Row>
        ))
      ) : (
        <PseudoStates status={sourceChildStatus} />
      )}

      <SimpleCollapse
        title="History"
        body={<CustodyHistory />}
        className="mt-3"
      />
    </Card>
  );
};

export default Custody;
